import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { CAPABILITIES } from "@/data/content";
import { NAV_SUBMENUS } from "@/data/servicePages";
import { INDUSTRIES } from "@/data/industries";
import { SOLUTIONS } from "@/data/solutions";

const SOLUTION_NAV = SOLUTIONS.map((s) => ({ slug: s.id, name: s.name, descriptor: s.descriptor }));

const RESOURCE_NAV = [
  { slug: "insights", name: "Insights", descriptor: "Long-form perspectives" },
  { slug: "tools", name: "Growth Tools", descriptor: "104 free calculators & kits" },
];

const ABOUT_LINKS = [
  { slug: "businesses/growth-partners", name: "BLEZZINGS Growth Partners" },
  { slug: "leadership", name: "Leadership" },
  { slug: "how-we-engage", name: "How We Engage" },
  { slug: "careers", name: "Careers" },
  { slug: "contact", name: "Contact" },
];
import LogoMark from "@/components/site/LogoMark";

export default function Nav({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // 'capabilities' | 'ecosystem' | null
  const [openSub, setOpenSub] = useState(null); // expanded sub-menu slug | null
  const closeTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
    setOpenSub(null);
  }, [location.pathname]);

  const open = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(key);
  };
  const toggle = (key) => setOpenMenu((v) => (v === key ? null : key));
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 150);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-40 transition-colors duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto pl-3 pr-3 sm:pl-6 sm:pr-8 md:pl-8 md:pr-12 h-16 sm:h-20 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center"
          aria-label="BLEZZINGS home"
        >
          <LogoMark
            variant="dark"
            className="h-10 sm:h-12 md:h-12 w-auto select-none"
            testid="nav-logo-mark"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavDropdown
            label="Our Services"
            heading="What We Do"
            items={CAPABILITIES}
            basePath="/capabilities"
            testidPrefix="nav-capabilities"
            menuKey="capabilities"
            openMenu={openMenu}
            openSub={openSub}
            onToggleSub={setOpenSub}
            onOpen={open}
            onToggle={toggle}
            onScheduleClose={scheduleClose}
          />
          <NavDropdown
            label="Industries"
            heading="Who We Help"
            items={INDUSTRIES}
            basePath="/industries"
            testidPrefix="nav-industries"
            menuKey="industries"
            openMenu={openMenu}
            openSub={openSub}
            onToggleSub={setOpenSub}
            onOpen={open}
            onToggle={toggle}
            onScheduleClose={scheduleClose}
          />
          <NavDropdown
            label="Solutions"
            heading="Focused Consulting Solutions"
            items={SOLUTION_NAV}
            basePath="/solutions"
            testidPrefix="nav-solutions"
            menuKey="solutions"
            openMenu={openMenu}
            openSub={openSub}
            onToggleSub={setOpenSub}
            onOpen={open}
            onToggle={toggle}
            onScheduleClose={scheduleClose}
          />
          <NavDropdown
            label="Resources"
            heading="Learn & Explore"
            items={RESOURCE_NAV}
            basePath=""
            testidPrefix="nav-resources"
            menuKey="resources"
            openMenu={openMenu}
            openSub={openSub}
            onToggleSub={setOpenSub}
            onOpen={open}
            onToggle={toggle}
            onScheduleClose={scheduleClose}
          />
          <NavDropdown
            label="About"
            heading="About BLEZZINGS"
            items={ABOUT_LINKS}
            basePath=""
            testidPrefix="nav-about"
            menuKey="about"
            openMenu={openMenu}
            openSub={openSub}
            onToggleSub={setOpenSub}
            onOpen={open}
            onToggle={toggle}
            onScheduleClose={scheduleClose}
          />
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            data-testid="nav-book-btn"
            onClick={onBook}
            className="hidden sm:inline-flex items-center gap-2 px-5 h-10 whitespace-nowrap bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors duration-300"
          >
            Book a Consultation
            <ArrowUpRight className="h-4 w-4" />
          </button>
          <button
            data-testid="nav-menu-toggle"
            className="md:hidden inline-flex items-center justify-center h-11 w-11 border border-zinc-300 text-black bg-white/70 hover:bg-black hover:text-white transition-colors"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden bg-white border-t border-black/5 max-h-[80vh] overflow-y-auto"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-zinc-500 mb-3">
                Our Services
              </div>
              <ul className="space-y-3">
                {CAPABILITIES.map((c) => {
                  const subs = NAV_SUBMENUS[c.slug] || null;
                  const subOpen = openSub === c.slug;
                  return (
                    <li key={c.slug}>
                      <div className="flex items-center justify-between gap-3">
                        <Link
                          to={`/capabilities/${c.slug}`}
                          data-testid={`nav-mobile-capabilities-${c.slug}`}
                          className="flex-1 text-base text-black"
                          onClick={() => setMobileOpen(false)}
                        >
                          <span className="font-serif-display font-bold">{c.name}</span>
                        </Link>
                        {subs ? (
                          <button
                            type="button"
                            data-testid={`nav-mobile-subtoggle-${c.slug}`}
                            onClick={() => setOpenSub(subOpen ? null : c.slug)}
                            aria-expanded={subOpen}
                            aria-label={`Toggle ${c.name} sub-menu`}
                            className="p-1 text-zinc-400 hover:text-black transition-colors"
                          >
                            <ChevronDown
                              className={`h-4 w-4 transition-transform duration-200 ${
                                subOpen ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                        ) : (
                          <ArrowUpRight className="h-4 w-4 text-zinc-400" />
                        )}
                      </div>
                      {subs && subOpen && (
                        <ul
                          data-testid={`nav-mobile-submenu-${c.slug}`}
                          className="mt-2 ml-2 border-l border-black/10 pl-4 space-y-2"
                        >
                          {subs.map((s) => (
                            <li key={s.slug}>
                              <Link
                                to={`/capabilities/${c.slug}/${s.slug}`}
                                data-testid={`nav-mobile-sub-${s.slug}`}
                                className="block py-1 text-sm text-zinc-600 hover:text-black transition-colors"
                                onClick={() => setMobileOpen(false)}
                              >
                                {s.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="border-t border-black/10 pt-5">
              <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-zinc-500 mb-3">
                Industries
              </div>
              <ul className="space-y-3">
                {INDUSTRIES.map((ind) => (
                  <li key={ind.slug}>
                    <Link
                      to={`/industries/${ind.slug}`}
                      data-testid={`nav-mobile-industries-${ind.slug}`}
                      className="flex items-center justify-between text-base text-black"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span className="font-serif-display font-bold">{ind.name}</span>
                      <ArrowUpRight className="h-4 w-4 text-zinc-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-black/10 pt-5">
              <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-zinc-500 mb-3">
                Solutions
              </div>
              <ul className="space-y-3">
                {SOLUTIONS.map((s) => (
                  <li key={s.id}>
                    <Link
                      to={`/solutions/${s.id}`}
                      data-testid={`nav-mobile-solution-${s.id}`}
                      className="flex items-start justify-between gap-3 text-base text-black"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span className="min-w-0">
                        <span className="font-serif-display font-bold">{s.name}</span>
                        <span className="block text-xs font-normal text-zinc-500 mt-0.5 leading-snug">
                          {s.descriptor}
                        </span>
                      </span>
                      <ArrowUpRight className="h-4 w-4 mt-1 shrink-0 text-zinc-400" />
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/solutions"
                    data-testid="nav-mobile-solutions"
                    className="flex items-center justify-between text-base text-black"
                    onClick={() => setMobileOpen(false)}
                  >
                    <span className="font-serif-display font-bold">All Solutions</span>
                    <ArrowUpRight className="h-4 w-4 text-zinc-400" />
                  </Link>
                </li>
              </ul>
            </div>
            <div className="border-t border-black/10 pt-5">
              <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-zinc-500 mb-3">
                Resources
              </div>
              <ul className="space-y-3">
                {[
                  { to: "/insights", testid: "nav-mobile-insights", label: "Insights" },
                  { to: "/tools", testid: "nav-mobile-tools", label: "Growth Tools" },
                ].map((l) => (
                  <li key={l.testid}>
                    <Link
                      to={l.to}
                      data-testid={l.testid}
                      className="flex items-center justify-between text-base text-black"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span className="font-serif-display font-bold">{l.label}</span>
                      <ArrowUpRight className="h-4 w-4 text-zinc-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-black/10 pt-5">
              <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-zinc-500 mb-3">
                About
              </div>
              <ul className="space-y-3">
                {[
                  { to: "/businesses/growth-partners", testid: "nav-mobile-about-growth-partners", label: "BLEZZINGS Growth Partners" },
                  { to: "/leadership", testid: "nav-mobile-leadership", label: "Leadership" },
                  { to: "/careers", testid: "nav-mobile-about-careers", label: "Careers" },
                  { to: "/how-we-engage", testid: "nav-mobile-about-how-we-engage", label: "How We Engage" },
                  { to: "/contact", testid: "nav-mobile-about-contact", label: "Contact" },
                  { to: "/", testid: "nav-mobile-home", label: "Home" },
                ].map((l) => (
                  <li key={l.testid}>
                    <Link
                      to={l.to}
                      data-testid={l.testid}
                      className="flex items-center justify-between text-base text-black"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span className="font-serif-display font-bold">{l.label}</span>
                      <ArrowUpRight className="h-4 w-4 text-zinc-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <button
              data-testid="nav-mobile-book-btn"
              onClick={() => {
                setMobileOpen(false);
                onBook();
              }}
              className="mt-2 inline-flex items-center justify-center gap-2 h-12 bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors duration-300"
            >
              Book a Consultation
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

function NavDropdown({
  label,
  heading,
  items,
  basePath,
  testidPrefix,
  menuKey,
  openMenu,
  openSub,
  onToggleSub,
  onOpen,
  onToggle,
  onScheduleClose,
}) {
  const isOpen = openMenu === menuKey;
  return (
    <div
      className="relative"
      onMouseEnter={() => onOpen(menuKey)}
      onMouseLeave={onScheduleClose}
    >
      <button
        data-testid={`${testidPrefix}-toggle`}
        onClick={() => onToggle(menuKey)}
        className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-zinc-700 hover:text-black transition-colors"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span>{label}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div
          data-testid={`${testidPrefix}-menu`}
          className="absolute left-1/2 -translate-x-1/2 top-full mt-4 w-[480px] bg-white border border-black/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.25)]"
          onMouseEnter={() => onOpen(menuKey)}
          onMouseLeave={onScheduleClose}
        >
          <div className="px-6 pt-5 pb-3 border-b border-black/5">
            <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-zinc-500">
              {heading}
            </span>
          </div>
          <ul className="py-2">
            {items.map((item) => {
              const subs = NAV_SUBMENUS[item.slug] || null;
              const subOpen = openSub === item.slug;
              return (
                <li key={item.slug}>
                  <div className="group flex items-start gap-4 px-6 py-3 hover:bg-[#F2F2F2] transition-colors">
                    <Link
                      to={`${basePath}/${item.slug}`}
                      data-testid={`${testidPrefix}-${item.slug}`}
                      className="flex-1 min-w-0"
                    >
                      <div className="font-serif-display font-bold text-lg text-black leading-tight">
                        {item.name}
                      </div>
                      {item.descriptor && (
                        <div className="text-[12px] text-zinc-500 mt-0.5 tracking-wide">
                          {item.descriptor}
                        </div>
                      )}
                    </Link>
                    {subs ? (
                      <button
                        type="button"
                        data-testid={`${testidPrefix}-subtoggle-${item.slug}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(menuKey);
                          onToggleSub(subOpen ? null : item.slug);
                        }}
                        aria-expanded={subOpen}
                        aria-label={`Toggle ${item.name} sub-menu`}
                        className="mt-1 p-1 text-zinc-400 hover:text-black transition-colors"
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            subOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    ) : (
                      <ArrowUpRight className="h-4 w-4 mt-1 text-zinc-400 group-hover:text-black transition-colors" />
                    )}
                  </div>
                  {subs && subOpen && (
                    <ul
                      data-testid={`nav-submenu-${item.slug}`}
                      className="border-t border-black/5 bg-[#FAFAFA] py-1"
                    >
                      {subs.map((s) => (
                        <li key={s.slug}>
                          <Link
                            to={`${basePath}/${item.slug}/${s.slug}`}
                            data-testid={`${testidPrefix}-sub-${s.slug}`}
                            className="flex items-center justify-between pl-12 pr-6 py-2 text-sm text-zinc-600 hover:text-black transition-colors"
                          >
                            {s.title}
                            <ArrowUpRight className="h-3.5 w-3.5 text-zinc-300" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
